import { useEffect, useState } from 'react';
import { getSubscription } from '@/services/household.service';
import { TIERS } from '@/constants/billing';
import type { SubscriptionTier } from '@/types';

/** The household's plan: its tier and that tier's monthly email cap. */
export interface EmailPlan {
  tier: SubscriptionTier;
  /** null = unlimited (FAMILY). */
  quota: number | null;
}

/**
 * Returns the household's plan — the subscription tier and its monthly
 * email quota — or `undefined` while loading. Same read and fallbacks as
 * `useEmailQuota` (no subscription row, or a failed read, → FREE). Use it
 * where the plan must be NAMED: the tier is the source of truth, never a
 * guess from the cap.
 *
 * Pass `refreshKey` to force a re-fetch (see `useEmailQuota`).
 */
export function useEmailPlan(
  householdId: string | null | undefined,
  refreshKey?: number,
): EmailPlan | undefined {
  const [plan, setPlan] = useState<EmailPlan | undefined>(undefined);

  useEffect(() => {
    if (!householdId) {
      setPlan(undefined);
      return;
    }
    let cancelled = false;
    getSubscription(householdId)
      .then((sub) => {
        if (cancelled) return;
        const tier: SubscriptionTier = sub?.tier ?? 'FREE';
        setPlan({ tier, quota: TIERS[tier].emailQuota });
      })
      .catch(() => {
        if (!cancelled) setPlan({ tier: 'FREE', quota: TIERS.FREE.emailQuota });
      });
    return () => {
      cancelled = true;
    };
  }, [householdId, refreshKey]);

  return plan;
}

/**
 * Returns the monthly email quota for the household's current
 * subscription tier:
 *   - number  → tier with a cap (FREE=20, CORE=100)
 *   - null    → unlimited (FAMILY) — caller should hide the bar
 *   - undefined → still loading; caller should render a placeholder
 *
 * The tier is fetched once per `householdId`. Defaults to the FREE
 * quota if no subscription row exists yet (new households without
 * billing data still get the meter rendered).
 *
 * Pass `refreshKey` (a monotonically changing number) to force a
 * re-fetch after a side-effect that the hook can't observe — e.g. an
 * admin tier override changing the household's tier while the page is
 * open, or a Stripe webhook landing after checkout.
 */
export function useEmailQuota(
  householdId: string | null | undefined,
  refreshKey?: number,
): number | null | undefined {
  const plan = useEmailPlan(householdId, refreshKey);
  return plan === undefined ? undefined : plan.quota;
}
